import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ElementRef,
    ViewChild
} from "@angular/core";
import { NgUserService } from "../../ng-services/ng-user.service";
import {
    ASR_CLOSE_EVENTS,
    ITCIAsrContext,
    ITCIAsrConfig,
    ITCIASRModelSchema,
    ITCIUserInfo,
    TCI_WS_ASR_VALIDATE_API_SPEC,
    TCI_WS_ASR_VALIDATE_OPERATION,
    TCI_WS_ASR_VALIDATE_RUNTIME
} from "@tci/common";
import { setTimeoutInZone } from "../../ng-util/tools";
import { Subject, BehaviorSubject } from "rxjs";

/**
 * NgAsrDialogComponent
 */
@Component({
    selector: "ng-asr-dialog",
    templateUrl: "./ng-asr-dialog.component.html",
    styleUrls: ["./ng-asr-dialog.component.less"],
})
export class NgAsrDialogComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    private isAsrComponentMounted = false; // From "mutationObserver"
    private userInfo: ITCIUserInfo;
    private asrConfig: ITCIAsrConfig;
    private modalInstance: any;
    notAvailable = false;

    @ViewChild("synergyModal", { read: ElementRef }) modal: ElementRef;

    // To tell the implementer that the ASR Syn library is loaded and ready
    // This emits at same time than "componentReady"
    private componentReady$ = new BehaviorSubject(false);

    // Emits the ASR selected schema
    @Output() selected: EventEmitter<ITCIASRModelSchema> = new EventEmitter();

    // Notifies on when the ASR modal is closed
    @Output() close: EventEmitter<null> = new EventEmitter();

    // Notifies when the ASR module is ready and available
    @Output() componentReady: EventEmitter<null> = new EventEmitter();

    /**
     * Cross-org sharing is ON by default, so this configuration is not necessary except for
     * illustration purposes.  If you want to opt-out, set this to false.
     * This demo does include a 2nd org in the TSC mock-up; you should see it in the drop-down.
     * The services in both orgs will be the same, however.
     * Note that the current org sharing configurations in TSC are probably not to customers' liking,
     * since all child orgs of a regular parent can share with each other, so you may want to turn this OFF.
     */
    @Input() crossOrgSharing = false;

    /**
     * Set this to true if your design-time captures and records "intercomRegion" from the dialog response,
     * and your runtime knows when to send "x-atmosphere-region={intercomRegion}" header
     * (only when intercomRegion is different from your deployed region).
     * This is only relevant for internal Troposphere tenants, at the moment; external tenants have no access
     * to intercom anyway.
     */
    @Input() globalIntercom = false;

    // Request the ASR modal oppening
    @Input() openModal = false;

    // If the ASR object is not available this message would be shown
    @Input() errorMessage = "ASR Component is not available";

    // URL Path of the ASR JS File
    @Input() asrJavaScriptFilesUrl = "https://localdev-integration.cloud.tibco.com/synergy-modules/asr/asr.esm.js";

    // Pre-Selected TCI operation to give to the ASR modal (empty for none)
    @Input() selectedOperation: ITCIASRModelSchema;

    /**
     * validateRuntime
     * Return "null" for validation pass
     *
     * validateRuntime: (extAuthz, intercom, assurance) => {
     *  if (intercom)
     *    return null;
     *  else
     *    return "TIBCO Cloud XYZ does not support this kind of service.";
     *  }
     */
    @Input() fnValidateRuntime: TCI_WS_ASR_VALIDATE_RUNTIME = (...args) => null;

    /**
     * validateApiSpec
     * Return "null" for validation pass
     * First argument is Swagger Schema
     *
     * apiSpec is the entire Swagger
     * Typical error use-cases:
     * 1. the apiSpec is OpenAPI 3.0, but you only support Swagger 2.0
     * 2. You require some extension field (e.g. "x-tcla-magic") and it is absent
     * Return a string (or a non-empty list of strings) to signal error.
     *
     * if (apiSpec.swagger === "2.0" || apiSpec.openapi === "3.0.0")
     *   return null;
     * else
     *   return "TIBCO Cloud XYZ does not support this kind of service.";
    */
    @Input() fnValidateApiSpec: TCI_WS_ASR_VALIDATE_API_SPEC = (...args) => null;

    /**
     * validateOperation
     * Return "null" for validation pass
     * First argument is Swagger Schema
     *
     * apiSpec is the entire Swagger
     * path is e.g. "/pet/{petId}"
     * operation is e.g. "get"
     * The operation details can be accessed as apiSpec[path][operation]
     * Return an error string (or an array of strings) if you cannot support the method or signature of the operation,
     * or if you require some optional field that is missing (e.g. "operationId")
     *
     * const op = apiSpec.paths[path][operation];
     * if (op.operationId)
     *   return null;
     * else
     *   return "This operation has no operationId." // I believe this is the use-case for TCLA
     */
    @Input() fnValidateOperation: TCI_WS_ASR_VALIDATE_OPERATION = (...args) => null;

    constructor(userSvc: NgUserService, private ngZone: NgZone) {
        userSvc.getUserInfo().subscribe(ui => this.userInfo = ui);
    }

    ngOnInit() {
        this.loadScripts();
    }

    ngAfterViewInit() {
        this.modalInstance = this.modal.nativeElement;
        this.mutationObserver();
    }

    ngOnChanges() {
        if (this.openModal) {
            this.openAsrModal();
        }
    }

    ngOnDestroy() {
        ASR_CLOSE_EVENTS.forEach(e => this.modalInstance.removeEventListener(e, () => this.closeModal()));
    }

    // Load the ASR JS module files
    private loadScripts() {
        // Check if the module is already loaded
        if (Array.from(document.querySelectorAll("script")).find(e => e.src === this.asrJavaScriptFilesUrl)) {
            this.componentReady.emit();
            this.componentReady$.next(true);
            return;
        }

        const sTag = "script";
        const lastTag = document.getElementsByTagName(sTag)[0];
        const newTag: any = document.createElement(sTag);
        newTag.type = "module";
        newTag.src = this.asrJavaScriptFilesUrl;
        newTag.onerror = $event => this.displayError($event);
        lastTag.parentNode.insertBefore(newTag, lastTag);
    }

    // Watch the DOM to know if the ASR component is ready
    private mutationObserver() {
        const mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(() => {
                if (this.modalInstance.getServiceDetails && !this.isAsrComponentMounted) {
                    ASR_CLOSE_EVENTS.forEach(e => this.modalInstance.addEventListener(e, () => this.closeModal()));
                    this.isAsrComponentMounted = true;
                    this.componentReady.emit();
                    this.componentReady$.next(true);
                    mutationObserver.disconnect();
                }
            });
        });

        mutationObserver.observe(this.modalInstance, {
            attributes: true,
            attributeOldValue: true
        });
    }

    // Set the ASR configuration data
    private initializeAsrConfig() {
        this.asrConfig = {
            modalConfig: {
                validateRuntime: this.fnValidateRuntime,
                validateApiSpec: this.fnValidateApiSpec,
                validateOperation: this.fnValidateOperation,
                crossOrgSharing: this.crossOrgSharing,
                globalIntercom: this.globalIntercom,
                onSelection: (selected: ITCIASRModelSchema) => {
                    setTimeoutInZone(this.ngZone, () => {
                        this.selected.emit(selected);
                        this.openModal = false;
                    });
                    return;
                }
            },
            openAsrModal: () => {
                const modalInstance: any = this.modal.nativeElement;
                if (modalInstance) {
                    try {
                        // This is the main method exposed on the web component.
                        // The single parameter must carry the configuration and callbacks.
                        // Note that the modal will open already focused on a service, if
                        // a valid "context" object is attached to the config parameter.
                        modalInstance.openModal(this.asrConfig.modalConfig);
                    } catch (e) {
                        console.error(e);
                    }
                }
            }
        };

        if ( // Set pre-selected context data
            this.selectedOperation &&
            this.selectedOperation.service &&
            this.selectedOperation.service.serviceId &&
            typeof this.selectedOperation.service.serviceId === "string" &&
            this.selectedOperation.service.serviceId.trim().length > 0
        ) {
            // The context object (for opening the modal with focus) requires subId, not gsbc.
            this.asrConfig.modalConfig.context = {
                serviceId: this.selectedOperation.service.serviceId,
                operation: this.selectedOperation.operation,
                path: this.selectedOperation.path,
                gsbc: this.selectedOperation.gsbc || this.userInfo.gsbc
            };
        }
    }

    // Pass the context to the ASR module and open the modal
    private openAsrModal() {
        if (this.isAsrComponentMounted) {
            this.initializeAsrConfig();
            this.asrConfig.openAsrModal();
        } else {
            console.error("ASR > The component is not available");
        }
    }

    private displayError($event) {
        this.notAvailable = true;
        console.error("ASR > The component's scripts are not present", $event);
    }

    private closeModal() {
        this.openModal = false;
        this.close.emit();
    }

    // Expose the Subject for implementer to know when the ASR Syn library is loaded and ready
    asrLibMounted(): Subject<boolean> {
        return this.componentReady$;
    }

    // For allowing implementer to call ASR Syn "getServiceDetails" method
    getServiceDetails(fnArgs: ITCIAsrContext) {
        return this.modalInstance.getServiceDetails(fnArgs);
    }
}
